// All content, trademarks, and data on this document are the property of Healthworks Analytics, LLC and are protected by applicable intellectual property laws. Unauthorized use, reproduction, or distribution of this material is strictly prohibited.
import { memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuX } from "react-icons/lu";
import { logAmplitudeEvent } from "../../utils/amplitude/functions";
import amplitude from "../../utils/amplitude/initiate";
import { userLogsAPI } from "../../services/session.service";
import { setIsAepDataUpdatePopActive } from "../../store/auth/action";

// The global `*` rule in index.css sets size and weight on every element, so
// the span needs its own size or it renders at 16px inside a 14px line.
const Bold = ({ children }) => (
  <span className="text-sm font-semibold leading-5">{children}</span>
);

const Text = ({ children }) => (
  <div className="self-stretch text-left text-general-foreground text-sm font-normal leading-5">
    {children}
  </div>
);

const content = {
  heading: "2027 Broker Commission Status Update",
  widthClass: "max-w-[670px]",
  sections: [
    <Text>
      The <Bold>2027 Broker Commission Status Dashboard</Bold> has been refreshed
      with all available commission status information received through{" "}
      <Bold>October 27, 2026</Bold>.
    </Text>,
    <Text>
      The dashboard reflects the latest available Commissioned and
      Non-Commissioned plan status and is timestamped accordingly.
    </Text>,
    <Text>
      <Bold>Enrollment Predictions have not been updated as part of this refresh.</Bold>{" "}
      You can continue to manually adjust commission status within the
      Predictions dashboard as needed. We are currently evaluating the best
      approach for incorporating refreshed broker commission status into
      Predictions without unexpectedly changing previously released prediction
      results.
    </Text>,
    <Text>
      We will provide an additional update if and when refreshed broker
      commission status is incorporated into Enrollment Predictions.
    </Text>,
    <Text>
      Note: Availability of individual dashboards, features, and updates may
      vary based on your HealthWorksAI subscription and contracted scope.
    </Text>,
  ],
};

function AepDataUpdatePop() {
  const { clientName, userDetails } = useSelector((state) => ({
    clientName: state.clientname,
    userDetails: state.userDetails,
  }));
  const dispatch = useDispatch();

  function onClose() {
    logAmplitudeEvent(amplitude, "2027 AEP Data Update Closed", {
      clientName,
      eventTimestamp: new Date().toLocaleString("en-US", { timeZone: "UTC" }),
    });
    userLogsAPI(
      "2027 AEP Data Update",
      "2027 AEP Data Update Closed",
      userDetails,
      clientName,
      "Closed"
    );
    dispatch(setIsAepDataUpdatePopActive(false));
  }

  return (
    <div className="fixed inset-0 z-[100] select-none flex items-center justify-center bg-black/50 p-4">
      <div className={`font-geist w-full ${content.widthClass} max-h-full bg-general-background rounded-[10px] shadow-[0px_4px_6px_-4px_rgba(0,0,0,0.10)] shadow-lg outline outline-1 outline-offset-[-1px] outline-general-border flex flex-col justify-start items-end overflow-hidden`}>
        <div className="self-stretch min-h-0 px-4 pt-4 flex justify-center items-start gap-2 overflow-y-auto">
          <div className="flex-1 p-2 flex flex-col justify-start items-start gap-4">
            <div className="self-stretch inline-flex justify-start items-center gap-2">
              <div className="flex-1 min-w-0 flex flex-col gap-1 text-left">
                <div className="text-general-secondary-foreground text-xl font-semibold leading-6">
                  {content.heading}
                </div>
                {content.lastUpdated && (
                  <div className="text-general-muted-foreground text-xs font-normal leading-4">
                    {content.lastUpdated}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="shrink-0 opacity-70 hover:opacity-100 ani"
              >
                <LuX className="size-4 text-general-muted-foreground" />
              </button>
            </div>

            <div className="self-stretch flex flex-col justify-start items-start gap-4">
              {content.sections.map((section, index) => (
                <div
                  key={index}
                  className="self-stretch flex flex-col justify-start items-start gap-2"
                >
                  {section}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="self-stretch shrink-0 p-4 flex flex-col justify-start items-start gap-2">
          <div className="self-stretch inline-flex justify-end items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="min-h-9 px-4 py-2 bg-general-primary rounded-lg hover:opacity-90 ani flex justify-center items-center gap-2"
            >
              <span className="text-center text-general-primary-foreground text-sm font-medium leading-5">
                Got it
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(AepDataUpdatePop);
